import { styled } from '@linaria/react';
import { ScrollArea } from '@mantine/core';
import type { MeasureDataByEntity } from '../../../../../api/work-periods-client/work-periods-client.type';
import {
  useEntityNames,
  useGroupedMeasures,
} from '../../../../../store/process-analysis-store/process-analysis-store.hooks';
import { baseWhite } from '../../../../../styles/design-tokens';
import { SUM_TYPE_MEASURES } from '../../../process-analysis.constants';
import { useScrollToTop } from '../../../process-analysis.hooks';
import { GroupSection } from './group-section';
import { TableHeader } from './table-header';
import { transformMonthlyAveragesData } from './table.helpers';
import { useFilteredMonthlyAveragesData, useSelectedEntities } from './table.hooks';

type Props = {
  data: MeasureDataByEntity | undefined;
};

export const MonthlyAveragesTable = ({ data }: Props) => {
  const scrollAreaRef = useScrollToTop();
  const groupedMeasures = useGroupedMeasures();
  const { entityIds, entityNames } = useEntityNames();
  const { selectedEntities, setSelectedEntities } = useSelectedEntities();

  const transformedData = transformMonthlyAveragesData(data, entityIds, groupedMeasures, SUM_TYPE_MEASURES);
  const filteredData = useFilteredMonthlyAveragesData(transformedData);

  return (
    <TableContainer>
      <StickyHeader>
        <TableHeader
          entityNames={entityNames}
          entityIds={entityIds}
          selectedEntities={selectedEntities}
          setSelectedEntities={setSelectedEntities}
        />
      </StickyHeader>

      <ScrollArea viewportRef={scrollAreaRef} h="100%" scrollbarSize={0} type="auto" offsetScrollbars>
        <ScrollableContent>
          {Object.entries(filteredData).map(([groupName, rows]) => (
            <GroupSection key={groupName} groupName={groupName} rows={rows} entityIds={entityIds} />
          ))}
        </ScrollableContent>
      </ScrollArea>
    </TableContainer>
  );
};

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${baseWhite};
  border-radius: 0;
  height: 100%;
  gap: 0;
  position: relative;
`;

const ScrollableContent = styled.div`
  flex: 1;
  padding-top: 44px;
  padding-bottom: 48px;
`;

const StickyHeader = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  background-color: ${baseWhite};
`;
